/**
 * Pure helpers for «عملکرد تیم فروش»: period presets (Jalali months through the
 * calendar package), metric definitions, ranking with ties, rank movement and
 * growth against the previous period. No React, no I/O. Money stays integer
 * Rial (BigInt); percentages are basis points (1% = 100) so nothing passes
 * through a floating-point number.
 */
import {
  fromJalali,
  jalaliMonthLength,
  subtractDays,
  toJalali,
  type BusinessDate,
} from '@sakhtar/calendar';
import type { PeriodMetrics, TeamMember } from '../../lib/types/commission.js';

export type PeriodPreset = 'thisMonth' | 'lastMonth' | 'last30' | 'last90' | 'thisYear';

export const PERIOD_PRESETS: readonly { readonly id: PeriodPreset; readonly label: string }[] = [
  { id: 'thisMonth', label: 'این ماه' },
  { id: 'lastMonth', label: 'ماه قبل' },
  { id: 'last30', label: '۳۰ روز اخیر' },
  { id: 'last90', label: '۹۰ روز اخیر' },
  { id: 'thisYear', label: 'امسال' },
];

export function periodFor(
  preset: PeriodPreset,
  today: BusinessDate,
): { readonly from: BusinessDate; readonly to: BusinessDate } {
  const { jy, jm } = toJalali(today);
  switch (preset) {
    case 'thisMonth':
      return { from: fromJalali({ jy, jm, jd: 1 }), to: today };
    case 'lastMonth': {
      const py = jm === 1 ? jy - 1 : jy;
      const pm = jm === 1 ? 12 : jm - 1;
      return {
        from: fromJalali({ jy: py, jm: pm, jd: 1 }),
        to: fromJalali({ jy: py, jm: pm, jd: jalaliMonthLength(py, pm) }),
      };
    }
    case 'last30':
      return { from: subtractDays(today, 29), to: today };
    case 'last90':
      return { from: subtractDays(today, 89), to: today };
    case 'thisYear':
      return { from: fromJalali({ jy, jm: 1, jd: 1 }), to: today };
  }
}

export type MetricKind = 'money' | 'count' | 'percent';

export interface MetricDef {
  readonly id: 'sales' | 'commission' | 'winRate' | 'collection' | 'activities' | 'won';
  readonly label: string;
  readonly hint: string;
  readonly kind: MetricKind;
  /** null = no data for this person (never ranked, never shown as zero). */
  readonly value: (m: PeriodMetrics) => bigint | null;
}

function ratioBp(numerator: bigint, denominator: bigint): bigint | null {
  if (denominator <= 0n) return null;
  return (numerator * 10_000n) / denominator;
}

export const METRICS: readonly MetricDef[] = [
  {
    id: 'sales',
    label: 'فروش',
    hint: 'مجموع فاکتورهای قطعی‌شده (بدون مالیات)',
    kind: 'money',
    value: (m) => BigInt(m.salesRial),
  },
  {
    id: 'commission',
    label: 'پورسانت',
    hint: 'پورسانت محاسبه‌شده روی فاکتورهای قطعی',
    kind: 'money',
    value: (m) => BigInt(m.commissionRial),
  },
  {
    id: 'winRate',
    label: 'نرخ برد',
    hint: 'برنده‌ها از میان سرنخ‌های بسته‌شده',
    kind: 'percent',
    value: (m) => ratioBp(BigInt(m.leadsWon), BigInt(m.leadsWon + m.leadsLost)),
  },
  {
    id: 'collection',
    label: 'وصول',
    hint: 'سهم وصول‌شده از فروش دورهٔ انتخاب‌شده',
    kind: 'percent',
    value: (m) => ratioBp(BigInt(m.collectedRial), BigInt(m.salesRial)),
  },
  {
    id: 'won',
    label: 'ارزش برنده‌ها',
    hint: 'مبلغ سرنخ‌های برنده‌شده',
    kind: 'money',
    value: (m) => BigInt(m.wonRial),
  },
  {
    id: 'activities',
    label: 'فعالیت',
    hint: 'تماس، جلسه و کارهای انجام‌شده',
    kind: 'count',
    value: (m) => BigInt(m.activitiesDone),
  },
];

export function metricById(id: MetricDef['id']): MetricDef {
  return METRICS.find((metric) => metric.id === id) ?? METRICS[0]!;
}

export interface Ranked {
  readonly member: TeamMember;
  readonly value: bigint | null;
  /** Competition ranking: ties share a rank (1, 2, 2, 4). null = not ranked. */
  readonly rank: number | null;
}

export function rankMembers(
  members: readonly TeamMember[],
  metric: MetricDef,
  pick: (member: TeamMember) => PeriodMetrics = (m) => m.current,
): readonly Ranked[] {
  const rows = members.map((member) => ({ member, value: metric.value(pick(member)) }));
  const scored = rows
    .filter(
      (row): row is { member: TeamMember; value: bigint } => row.value !== null && row.value > 0n,
    )
    .sort((a, b) => (a.value === b.value ? 0 : a.value > b.value ? -1 : 1));
  const ranks = new Map<string, number>();
  scored.forEach((row, index) => {
    const tied = index > 0 && scored[index - 1]!.value === row.value;
    ranks.set(
      row.member.salespersonId,
      tied ? ranks.get(scored[index - 1]!.member.salespersonId)! : index + 1,
    );
  });
  const ordered = [
    ...scored.map((row) => ({ ...row, rank: ranks.get(row.member.salespersonId)! })),
    ...rows
      .filter((row) => !ranks.has(row.member.salespersonId))
      .sort((a, b) => a.member.name.localeCompare(b.member.name, 'fa'))
      .map((row) => ({ ...row, rank: null })),
  ];
  return ordered;
}

/** Positive = climbed. null when the person was unranked in either period. */
export function rankMovement(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null) return null;
  return previous - current;
}

/** Growth in basis points; null when there is no previous value to compare. */
export function growthBp(current: bigint, previous: bigint): bigint | null {
  if (previous <= 0n) return null;
  return ((current - previous) * 10_000n) / previous;
}

/** Share of a target reached, in basis points (can exceed 10 000). */
export function targetProgressBp(salesRial: string, targetRial: string | null): bigint | null {
  if (targetRial === null) return null;
  const target = BigInt(targetRial);
  if (target <= 0n) return null;
  return (BigInt(salesRial) * 10_000n) / target;
}

/** Bar width 0–100 of `value` against the leader's value. */
export function barPercent(value: bigint | null, leader: bigint | null): number {
  if (value === null || leader === null || leader <= 0n || value <= 0n) return 0;
  const percent = Number((value * 1000n) / leader) / 10;
  return Math.min(100, Math.max(2, percent));
}

/** Sparkline points 0–100 (SVG y grows downward, so the caller flips). */
export function sparkPoints(values: readonly string[]): readonly number[] {
  const nums = values.map((v) => BigInt(v));
  const max = nums.reduce((a, b) => (b > a ? b : a), 0n);
  if (max <= 0n) return nums.map(() => 0);
  return nums.map((n) => Number((n * 1000n) / max) / 10);
}

export const METHOD_LABELS = {
  product_rate: 'نرخ هر محصول',
  percent_of_sale: 'درصد از کل فروش',
  fixed_per_invoice: 'مبلغ ثابت از هر فاکتور',
  tiered: 'پلکانی (بر اساس مبلغ فاکتور)',
} as const;

export const RELEASE_LABELS = {
  on_collection: 'هم‌زمان با وصول مبلغ فاکتور',
  on_invoice: 'همان لحظهٔ قطعی شدن فاکتور',
} as const;

/** Display digits (Rial or Toman) → integer Rial digits. */
export function displayToRial(digits: string, unit: 'rial' | 'toman'): string {
  const clean = digits.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
  if (clean === '') return '';
  return unit === 'toman' && clean !== '0' ? `${clean}0` : clean;
}

/** Integer Rial digits → digits in the display unit (Toman floors; plans are saved ×10). */
export function rialToDisplay(rial: string | null, unit: 'rial' | 'toman'): string {
  if (rial === null || rial === '') return '';
  if (unit === 'rial') return rial;
  return (BigInt(rial) / 10n).toString();
}
